import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchAdminMembers,
  fetchAdminMemberDetail,
  adjustMemberPoints,
  deleteAdminMember,
  editAdminMember,
  fetchFraudMemberDetail,
  setFraudTier,
  adjustMemberTrustScore,
  fetchMemberAttributes,
  upsertMemberAttribute,
  fetchMemberSessions,
  fetchMemberTrustHistory,
  fetchMemberDemographics,
  type MemberDemographics,
} from '../../lib/adminApi';
import { Search, Trash2, ArrowLeft } from 'lucide-react';
import { useParams, useNavigate, Link } from 'react-router-dom';

// ── Tab Components ──────────────────────────────────────────────────────────

function FraudTab({ memberId }: { memberId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'fraud-detail', memberId],
    queryFn: () => fetchFraudMemberDetail(memberId),
  });

  const [tierOverride, setTierOverride] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [expandedSignal, setExpandedSignal] = useState<string | null>(null);

  async function handleOverride() {
    if (!tierOverride || !overrideReason.trim()) return;
    setSubmitting(true);
    try {
      await setFraudTier(memberId, tierOverride, overrideReason.trim());
      setTierOverride('');
      setOverrideReason('');
      setShowConfirm(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'fraud-detail', memberId] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'member', memberId] });
    } catch { /* ignore */ }
    setSubmitting(false);
  }

  if (isLoading) return <div className="text-slate-400 py-4">Loading fraud data...</div>;
  if (!data) return <div className="text-slate-500 py-4">No fraud data available</div>;

  const tierColors: Record<string, string> = {
    clean: 'bg-green-900/50 text-green-400',
    watch: 'bg-amber-900/50 text-amber-400',
    shadow: 'bg-orange-900/50 text-orange-400',
    banned: 'bg-red-900/50 text-red-400',
  };

  const inputCls = 'w-full bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700';
  const selectCls = 'bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700';

  return (
    <div className="space-y-4">
      {/* Current tier + score */}
      <div className="flex items-center gap-3">
        <span className={`text-sm px-3 py-1 rounded-full font-medium ${tierColors[data.fraudTier] ?? 'bg-slate-700 text-slate-300'}`}>
          {data.fraudTier.toUpperCase()}
        </span>
        <span className="text-2xl font-bold text-white">{data.fraudScore}</span>
        <span className="text-sm text-slate-400">fraud score</span>
      </div>

      {/* Manual override */}
      <div className="border border-slate-700 rounded-lg p-3 space-y-2">
        <h4 className="text-xs text-slate-400 uppercase tracking-wide">Manual Override</h4>
        <div className="flex gap-2">
          <select value={tierOverride} onChange={(e) => setTierOverride(e.target.value)} className={`flex-1 ${selectCls}`}>
            <option value="">Select tier...</option>
            <option value="clean">Clean</option>
            <option value="watch">Watch</option>
            <option value="shadow">Shadow</option>
            <option value="banned">Banned</option>
          </select>
        </div>
        <input
          type="text"
          value={overrideReason}
          onChange={(e) => setOverrideReason(e.target.value)}
          placeholder="Reason (required)"
          className={inputCls}
        />
        {!showConfirm ? (
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!tierOverride || !overrideReason.trim()}
            className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 w-full"
          >
            Apply Override
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => void handleOverride()}
              disabled={submitting}
              className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {submitting ? 'Applying...' : `Confirm → ${tierOverride}`}
            </button>
            <button onClick={() => setShowConfirm(false)} className="px-4 py-2 rounded-lg text-sm bg-slate-700 text-slate-300">
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Signals table */}
      <div>
        <h4 className="text-xs text-slate-400 uppercase tracking-wide mb-2">Signals ({data.signals.length})</h4>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {data.signals.map((s: { id: string; signalType: string; confidence: string; severity: string; expiresAt: string | null; createdAt: string; isExpired: boolean; details: Record<string, unknown>; aiRationale: string | null }) => (
            <div key={s.id} className={`text-xs border-b border-slate-800 py-1.5 ${s.isExpired ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-2">
                <span className="text-slate-200">{new Date(s.createdAt).toLocaleString()}</span>
                <span className="text-slate-300 font-medium">{s.signalType}</span>
                <span className="text-slate-500">{s.confidence}/{s.severity}</span>
                {s.expiresAt && <span className="text-slate-600">{s.isExpired ? '(Expired)' : `exp ${new Date(s.expiresAt).toLocaleDateString()}`}</span>}
              </div>
              {s.aiRationale && (
                <div>
                  <button onClick={() => setExpandedSignal(expandedSignal === s.id ? null : s.id)} className="text-primary text-xs hover:underline">
                    {expandedSignal === s.id ? 'Hide' : 'AI Note'}
                  </button>
                  {expandedSignal === s.id && <p className="text-slate-400 mt-1">{s.aiRationale}</p>}
                </div>
              )}
            </div>
          ))}
          {data.signals.length === 0 && <p className="text-slate-500">No signals recorded</p>}
        </div>
      </div>

      {/* Device Fingerprints */}
      <div>
        <h4 className="text-xs text-slate-400 uppercase tracking-wide mb-2">Device Fingerprints</h4>
        <div className="space-y-1">
          {data.deviceFingerprints.map((f: { fingerprintHash: string; firstSeenAt: string; lastSeenAt: string; collisionCount: number }) => (
            <div key={f.fingerprintHash} className="flex justify-between text-xs py-1 border-b border-slate-800">
              <span className="text-slate-300 font-mono">{f.fingerprintHash}</span>
              <span className="text-slate-500">{new Date(f.firstSeenAt).toLocaleDateString()} — {new Date(f.lastSeenAt).toLocaleDateString()}</span>
              <span className={f.collisionCount > 0 ? 'text-orange-400 font-medium' : 'text-slate-600'}>
                {f.collisionCount} collision{f.collisionCount !== 1 ? 's' : ''}
              </span>
            </div>
          ))}
          {data.deviceFingerprints.length === 0 && <p className="text-slate-500 text-xs">No fingerprints</p>}
        </div>
      </div>

      {/* Override History */}
      {data.overrideHistory.length > 0 && (
        <div>
          <h4 className="text-xs text-slate-400 uppercase tracking-wide mb-2">Override History</h4>
          <div className="space-y-1">
            {data.overrideHistory.map((o: { tier: string; reason: string; createdAt: string }, i: number) => (
              <div key={i} className="text-xs py-1 border-b border-slate-800">
                <span className="text-slate-300">{new Date(o.createdAt).toLocaleString()}</span>
                <span className="text-slate-400 ml-2">→ {o.tier}</span>
                <span className="text-slate-500 ml-2">{o.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TrustTab({ memberId, currentScore }: { memberId: string; currentScore: number }) {
  const queryClient = useQueryClient();
  const { data: historyData, isLoading } = useQuery({
    queryKey: ['admin', 'trust-history', memberId],
    queryFn: () => fetchMemberTrustHistory(memberId),
  });

  const [adjustment, setAdjustment] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const trustLevel = currentScore >= 91 ? 5 : currentScore >= 61 ? 4 : currentScore >= 41 ? 3 : currentScore >= 26 ? 2 : 1;
  const levelNames: Record<number, string> = { 1: 'Basic', 2: 'Rising', 3: 'Trusted', 4: 'Elite', 5: 'Champion' };

  async function handleAdjust() {
    const delta = parseInt(adjustment, 10);
    if (!delta || !reason.trim()) return;
    setSubmitting(true);
    try {
      await adjustMemberTrustScore(memberId, delta, reason.trim());
      setAdjustment('');
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'trust-history', memberId] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'member', memberId] });
    } catch { /* ignore */ }
    setSubmitting(false);
  }

  const inputCls = 'w-full bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700';

  return (
    <div className="space-y-4">
      {/* Current score */}
      <div className="flex items-center gap-3">
        <span className="text-3xl font-bold text-white">{currentScore}</span>
        <div>
          <span className="text-sm text-slate-300 font-medium">Level {trustLevel} — {levelNames[trustLevel]}</span>
          <p className="text-xs text-slate-500">
            {trustLevel === 1 && '0–25: New member, building history'}
            {trustLevel === 2 && '26–40: Establishing track record'}
            {trustLevel === 3 && '41–60: Trusted, early redemption unlocked'}
            {trustLevel === 4 && '61–90: Highly trusted member'}
            {trustLevel === 5 && '91–100: Top-tier, instant credit'}
          </p>
        </div>
      </div>

      {/* Manual adjustment */}
      <div className="border border-slate-700 rounded-lg p-3 space-y-2">
        <h4 className="text-xs text-slate-400 uppercase tracking-wide">Adjust Trust Score</h4>
        <div className="flex gap-2">
          <input
            type="number"
            value={adjustment}
            onChange={(e) => setAdjustment(e.target.value)}
            placeholder="+10 or -15"
            className={`w-28 ${inputCls}`}
          />
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required)"
            className={`flex-1 ${inputCls}`}
          />
        </div>
        <p className="text-xs text-slate-600">Delta adjustment, not absolute. Result clamped 0–100.</p>
        <button
          onClick={() => void handleAdjust()}
          disabled={submitting || !adjustment || !reason.trim()}
          className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 w-full"
        >
          {submitting ? 'Applying...' : 'Apply Adjustment'}
        </button>
      </div>

      {/* Trust history */}
      <div>
        <h4 className="text-xs text-slate-400 uppercase tracking-wide mb-2">Trust Score History (last 20)</h4>
        {isLoading ? (
          <p className="text-slate-400 text-sm">Loading...</p>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {(historyData?.logs ?? []).map((log: { id: string; delta: number; score_after: number; reason: string; source_type: string; source_id: string | null; created_at: string }) => (
              <div key={log.id} className="text-xs py-1.5 border-b border-slate-800">
                <div className="flex justify-between">
                  <span className="text-slate-300">{new Date(log.created_at).toLocaleString()}</span>
                  <span className={log.delta >= 0 ? 'text-green-400' : 'text-red-400'}>
                    {log.delta >= 0 ? '+' : ''}{log.delta}
                  </span>
                  <span className="text-slate-500">{log.source_type}</span>
                  <span className="text-white font-medium">{log.score_after}</span>
                </div>
                {log.source_id && <p className="text-slate-500 mt-0.5 truncate">{log.source_id}</p>}
              </div>
            ))}
            {(historyData?.logs ?? []).length === 0 && <p className="text-slate-500">No trust score history</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function AttributesTab({ memberId }: { memberId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'attributes', memberId],
    queryFn: () => fetchMemberAttributes(memberId),
  });

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [addMode, setAddMode] = useState(false);
  const [addKey, setAddKey] = useState('');
  const [addValue, setAddValue] = useState('');
  const [addNotes, setAddNotes] = useState('');

  async function handleSave(key: string) {
    if (!editValue.trim() || !editNotes.trim()) return;
    setSubmitting(true);
    try {
      await upsertMemberAttribute(memberId, key, editValue.trim(), editNotes.trim());
      setEditingKey(null);
      setEditValue('');
      setEditNotes('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'attributes', memberId] });
    } catch { /* ignore */ }
    setSubmitting(false);
  }

  async function handleAdd() {
    if (!addKey.trim() || !addValue.trim() || !addNotes.trim()) return;
    setSubmitting(true);
    try {
      await upsertMemberAttribute(memberId, addKey.trim(), addValue.trim(), addNotes.trim());
      setAddMode(false);
      setAddKey('');
      setAddValue('');
      setAddNotes('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'attributes', memberId] });
    } catch { /* ignore */ }
    setSubmitting(false);
  }

  if (isLoading) return <div className="text-slate-400 py-4">Loading attributes...</div>;

  const inputCls = 'w-full bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700';
  const memberAttrs = data?.memberAttributes ?? [];
  const inferredAttrs = data?.inferredAttributes ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-xs text-slate-400 uppercase tracking-wide mb-2">Member Attributes ({memberAttrs.length})</h4>
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {memberAttrs.map((a: { id: string; attribute_key: string; attribute_value: string; source: string; locked: boolean; updated_at: string }) => (
            <div key={a.id} className="text-xs py-1.5 border-b border-slate-800">
              {editingKey === a.attribute_key ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-300 font-medium">{a.attribute_key}</span>
                    <span className="text-slate-600">({a.source})</span>
                  </div>
                  <input type="text" value={editValue} onChange={(e) => setEditValue(e.target.value)} placeholder="New value" className={inputCls} />
                  <input type="text" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Reason (required)" className={inputCls} />
                  <div className="flex gap-2">
                    <button onClick={() => void handleSave(a.attribute_key)} disabled={submitting || !editValue.trim() || !editNotes.trim()} className="bg-primary text-white px-3 py-1 rounded text-xs disabled:opacity-50">
                      {submitting ? '...' : 'Save'}
                    </button>
                    <button onClick={() => setEditingKey(null)} className="bg-slate-700 text-slate-300 px-3 py-1 rounded text-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <span className="text-slate-300">{a.attribute_key}</span>
                    <span className="text-slate-500 ml-2">{a.attribute_value.slice(0, 40)}</span>
                    <span className="text-slate-600 ml-2">({a.source})</span>
                  </div>
                  <button
                    onClick={() => { setEditingKey(a.attribute_key); setEditValue(a.attribute_value); setEditNotes(''); }}
                    className="text-primary text-xs hover:underline ml-2 shrink-0"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {inferredAttrs.length > 0 && (
        <div>
          <h4 className="text-xs text-slate-400 uppercase tracking-wide mb-2">Inferred Attributes ({inferredAttrs.length})</h4>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {inferredAttrs.map((a: { id: string; attribute_key: string; attribute_value: string; confidence_score: number; created_at: string }) => (
              <div key={a.id} className="flex justify-between text-xs py-1 border-b border-slate-800">
                <span className="text-slate-300">{a.attribute_key}</span>
                <span className="text-slate-500">
                  {a.attribute_value.slice(0, 30)}
                  <span className="text-slate-600 ml-1">({(a.confidence_score * 100).toFixed(0)}%)</span>
                  <span className="ml-1 px-1 py-0.5 bg-slate-700 rounded text-slate-400">Inferred</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add attribute */}
      {addMode ? (
        <div className="border border-slate-700 rounded-lg p-3 space-y-2">
          <h4 className="text-xs text-slate-400 uppercase tracking-wide">Add Attribute</h4>
          <input type="text" value={addKey} onChange={(e) => setAddKey(e.target.value)} placeholder="Attribute key" className={inputCls} />
          <input type="text" value={addValue} onChange={(e) => setAddValue(e.target.value)} placeholder="Value" className={inputCls} />
          <input type="text" value={addNotes} onChange={(e) => setAddNotes(e.target.value)} placeholder="Reason (required)" className={inputCls} />
          <div className="flex gap-2">
            <button onClick={() => void handleAdd()} disabled={submitting || !addKey.trim() || !addValue.trim() || !addNotes.trim()} className="flex-1 bg-primary text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50">
              {submitting ? '...' : 'Add'}
            </button>
            <button onClick={() => setAddMode(false)} className="px-4 py-2 rounded-lg text-sm bg-slate-700 text-slate-300">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAddMode(true)} className="text-primary text-sm hover:underline">+ Add Attribute</button>
      )}
    </div>
  );
}

function SessionsTab({ memberId }: { memberId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'sessions', memberId],
    queryFn: () => fetchMemberSessions(memberId),
  });

  if (isLoading) return <div className="text-slate-400 py-4">Loading sessions...</div>;

  const sessions = data?.sessions ?? [];

  const outcomeColors: Record<string, string> = {
    complete: 'text-green-400',
    completed: 'text-green-400',
    screener_fail: 'text-yellow-400',
    quality_fail: 'text-red-400',
    quota_full: 'text-slate-400',
    abandoned: 'text-slate-500',
    pending: 'text-slate-500',
  };

  function formatDuration(seconds: number | null): string {
    if (seconds === null) return '—';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  }

  return (
    <div>
      <h4 className="text-xs text-slate-400 uppercase tracking-wide mb-2">Recent Sessions ({sessions.length})</h4>
      <div className="space-y-1 max-h-80 overflow-y-auto">
        {sessions.map((s: { id: string; exchange: string; surveyId: string; status: string; points: number | null; startedAt: string; durationSeconds: number | null }) => (
          <div key={s.id} className="text-xs py-1.5 border-b border-slate-800">
            <div className="flex justify-between">
              <span className="text-slate-300">{new Date(s.startedAt).toLocaleString()}</span>
              <span className="text-slate-500">{s.exchange}</span>
              <span className="text-slate-400 font-mono">{s.surveyId.slice(0, 12)}</span>
              <span className={outcomeColors[s.status] ?? 'text-slate-400'}>{s.status}</span>
              <span className="text-white">{s.points ?? '—'}</span>
              <span className="text-slate-500">{formatDuration(s.durationSeconds)}</span>
            </div>
          </div>
        ))}
        {sessions.length === 0 && <p className="text-slate-500">No sessions found</p>}
      </div>
    </div>
  );
}

// ── Fraud Banner ────────────────────────────────────────────────────────────

function FraudBanner({ fraudTier, fraudScore }: { fraudTier: string; fraudScore: number }) {
  if (fraudTier === 'clean' && fraudScore === 0) return null;

  if (fraudTier === 'watch') {
    return (
      <div className="bg-amber-900/40 border border-amber-700 rounded-lg px-4 py-2 text-sm text-amber-300">
        ⚠ Fraud Watch — Score: {fraudScore}
      </div>
    );
  }

  if (fraudTier === 'shadow') {
    return (
      <div className="bg-orange-900/40 border border-orange-700 rounded-lg px-4 py-2 text-sm text-orange-300">
        🚨 Shadow — Score: {fraudScore} — Degraded experience active
      </div>
    );
  }

  if (fraudTier === 'banned') {
    return (
      <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-2 text-sm text-red-300">
        🚫 Banned — Score: {fraudScore} — Redemptions blocked
      </div>
    );
  }

  if (fraudScore > 0) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-slate-400">
        Fraud score: {fraudScore} (below watch threshold)
      </div>
    );
  }

  return null;
}

// ── Main Drawer ─────────────────────────────────────────────────────────────

export function AdminMemberDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const memberId = id;
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'member', memberId],
    queryFn: () => fetchAdminMemberDetail(memberId),
    enabled: !!memberId,
  });
  const queryClient = useQueryClient();

  // Adjust points
  const [adjustDelta, setAdjustDelta] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  // Edit fields
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editing, setEditing] = useState(false);
  const [editInit, setEditInit] = useState(false);

  // Delete
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Tabs
  const [activeTab, setActiveTab] = useState<'fraud' | 'trust' | 'attributes' | 'sessions'>('fraud');

  // Init edit fields from data
  if (data && !editInit) {
    setEditName(data.member.display_name ?? '');
    setEditPhone(data.member.phone_number ?? '');
    setEditStatus(data.member.status);
    setEditRole(data.member.role);
    setEditNotes(data.member.admin_notes ?? '');
    setEditInit(true);
  }

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'member', memberId] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'members'] });
  }

  async function handleAdjust() {
    const delta = parseInt(adjustDelta, 10);
    if (!delta || !adjustReason.trim()) return;
    setAdjusting(true);
    try {
      await adjustMemberPoints(memberId, delta, adjustReason.trim());
      setAdjustDelta('');
      setAdjustReason('');
      invalidate();
    } catch { /* ignore */ }
    setAdjusting(false);
  }

  async function handleEdit() {
    setEditing(true);
    try {
      await editAdminMember(memberId, {
        display_name: editName || undefined,
        phone_number: editPhone || undefined,
        status: editStatus || undefined,
        role: editRole || undefined,
        admin_notes: editNotes,
      });
      invalidate();
    } catch { /* ignore */ }
    setEditing(false);
  }

  async function handleDelete() {
    if (deleteConfirm !== 'DELETE') return;
    setDeleting(true);
    try {
      await deleteAdminMember(memberId);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'members'] });
      navigate('/admin/members');
    } catch { /* ignore */ }
    setDeleting(false);
  }

  const inputCls = 'w-full bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700';
  const selectCls = 'bg-slate-800 text-white rounded-lg px-3 py-2 text-sm border border-slate-700';

  const cardCls = 'bg-slate-900 rounded-xl border border-slate-800 p-5';
  const sectionH3 = 'text-xs text-slate-400 uppercase tracking-wide mb-3';

  return (
    <div className="space-y-5">
      <Link to="/admin/members" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white">
        <ArrowLeft className="w-3 h-3" /> Back to Members
      </Link>

      {isLoading ? (
        <div className={`${cardCls} text-slate-400`}>Loading member...</div>
      ) : !data ? (
        <div className={cardCls}>
          <p className="text-white font-medium">Member not found</p>
          <p className="text-slate-500 text-sm mt-1">This member may have been deleted, or the link is out of date.</p>
        </div>
      ) : (
        <>
          {/* Header — identity, status/role, at-a-glance state */}
          <div className={cardCls}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h1 className="text-white text-lg font-bold font-display truncate">{data.member.display_name ?? data.member.email ?? data.member.phone_number}</h1>
                <p className="text-sm text-slate-400 truncate">{data.member.email}{data.member.phone_number ? ` (${data.member.phone_number})` : ''}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs px-2.5 py-1 rounded-full ${data.member.status === 'active' ? 'bg-green-900/50 text-green-400' : 'bg-slate-700 text-slate-300'}`}>{data.member.status}</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-700 text-slate-300 capitalize">{data.member.role}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
              <div className="bg-slate-800 rounded-lg px-3 py-2">
                <p className="text-[11px] text-slate-500 uppercase tracking-wide">Balance</p>
                <p className="text-white font-semibold tabular-nums">{data.member.total_points.toLocaleString()} pts</p>
              </div>
              <div className="bg-slate-800 rounded-lg px-3 py-2">
                <p className="text-[11px] text-slate-500 uppercase tracking-wide">Trust score</p>
                <p className="text-white font-semibold tabular-nums">{data.member.trust_score}</p>
              </div>
              <div className="bg-slate-800 rounded-lg px-3 py-2">
                <p className="text-[11px] text-slate-500 uppercase tracking-wide">Identity</p>
                <p className={`font-semibold ${data.member.identity_verified ? 'text-emerald-400' : 'text-slate-400'}`}>{data.member.identity_verified ? 'Verified' : 'Not verified'}</p>
              </div>
            </div>
          </div>

            {/* Fraud Banner */}
            <FraudBanner fraudTier={data.member.fraud_tier} fraudScore={data.member.fraud_score} />

            {/* Identity Verification (Didit, Phase B) — read-only */}
            <div className={cardCls}>
              <h3 className={sectionH3}>Identity Verification</h3>
              {data.member.identity_verified ? (
                <p className="text-sm text-emerald-400 font-medium">
                  ✓ Verified
                  {data.member.identity_verified_at ? ` · ${new Date(data.member.identity_verified_at).toLocaleDateString()}` : ''}
                  {data.member.identity_verified_source ? ` · via ${data.member.identity_verified_source}` : ''}
                </p>
              ) : (
                <p className="text-sm text-slate-400">Not verified</p>
              )}
              {Array.isArray(data.identitySessions) && data.identitySessions.length > 0 ? (
                <div className="mt-2 space-y-2">
                  {(data.identitySessions as Array<{ session_id: string; status: string; face_match_score: number | null; liveness_score: number | null; created_at: string }>).map((s) => (
                    <div key={s.session_id} className="bg-slate-800 rounded-lg px-3 py-2 border border-slate-700 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-300">Status: {s.status}</span>
                        <span className="text-slate-500">{new Date(s.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="text-slate-400 mt-1">
                        Face match: {s.face_match_score ?? '—'} · Liveness: {s.liveness_score ?? '—'}
                      </div>
                      <div className="mt-1">
                        <span className="text-slate-500">Didit session: </span>
                        <code className="text-slate-300 font-mono break-all select-all">{s.session_id}</code>
                      </div>
                    </div>
                  ))}
                  <p className="text-[11px] text-slate-500">
                    Look the session ID up in Didit&apos;s console. The member-facing verify.didit.me page expires and is not linkable.
                  </p>
                </div>
              ) : (
                <p className="text-xs text-slate-600 mt-1">No verification sessions.</p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
            {/* ACTIONS — edit controls */}
            <div className="lg:col-span-2 space-y-5">
            {/* Edit Member */}
            <div className={cardCls}>
              <h3 className={sectionH3}>Edit Member</h3>
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Email</label>
                  <p className="text-sm text-slate-300 bg-slate-800 rounded-lg px-3 py-2 border border-slate-700">{data.member.email ?? '—'}</p>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-slate-500 block mb-1">Display Name</label>
                    <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Display name" className={inputCls} />
                  </div>
                  <div className="w-36">
                    <label className="text-xs text-slate-500 block mb-1">Phone</label>
                    <input type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="Phone" className={inputCls} />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-slate-500 block mb-1">Status</label>
                    <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)} className={`w-full ${selectCls}`}>
                      <option value="active">Active</option>
                      <option value="suspended">Suspended</option>
                      <option value="blocked">Blocked</option>
                      <option value="inactive">Inactive</option>
                      <option value="opted_out">Opted Out</option>
                    </select>
                  </div>
                  <div className="w-28">
                    <label className="text-xs text-slate-500 block mb-1">Role</label>
                    <select value={editRole} onChange={(e) => setEditRole(e.target.value)} className={`w-full ${selectCls}`}>
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <button onClick={() => void handleEdit()} disabled={editing} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 w-full">
                  {editing ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {/* Admin Notes */}
            <div className={cardCls}>
              <h3 className={sectionH3}>Admin Notes</h3>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={3}
                placeholder="Internal notes about this member..."
                className={inputCls + ' resize-none'}
              />
              <p className="text-xs text-slate-600 mt-1">Saved with Edit Member above</p>
            </div>

            {/* Adjust Points */}
            <div className={cardCls}>
              <h3 className={sectionH3}>Adjust Points</h3>
              <div className="flex gap-2">
                <input type="number" value={adjustDelta} onChange={(e) => setAdjustDelta(e.target.value)} placeholder="+100 or -50" className={`w-24 ${inputCls}`} />
                <input type="text" value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} placeholder="Reason (required)" className={`flex-1 ${inputCls}`} />
                <button onClick={() => void handleAdjust()} disabled={adjusting || !adjustReason.trim()} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                  {adjusting ? '...' : 'Apply'}
                </button>
              </div>
            </div>

            </div>
            {/* CONTEXT — read-only */}
            <div className="space-y-5">
            {/* Acquisition */}
            {data.acquisition && (
              <div className={cardCls}>
                <h3 className={sectionH3}>Acquisition</h3>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between py-1 border-b border-slate-800">
                    <span className="text-slate-400">Source</span>
                    <span className="text-slate-200 font-mono">
                      {data.acquisition.utm_source ?? 'organic'}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800">
                    <span className="text-slate-400">Campaign</span>
                    <span className="text-slate-200">
                      {data.acquisition.campaign?.name ?? '\u2014'}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-slate-800">
                    <span className="text-slate-400">Acquired via</span>
                    <span className="text-slate-200 capitalize">
                      {data.acquisition.acquired_via}
                    </span>
                  </div>
                  {data.acquisition.referred_by && (
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">Referred by</span>
                      <button
                        onClick={() => {
                          const r = data.acquisition?.referred_by;
                          if (r) navigate(`/admin/members/${r.id}`);
                        }}
                        className="text-primary hover:underline"
                      >
                        {data.acquisition.referred_by.email ?? data.acquisition.referred_by.display_name ?? data.acquisition.referred_by.id}
                      </button>
                    </div>
                  )}
                  {data.acquisition.aff_id && (
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">Affiliate ID</span>
                      <span className="text-slate-200 font-mono">{data.acquisition.aff_id}</span>
                    </div>
                  )}
                  {data.acquisition.session_id && (
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">Click session</span>
                      <span className="text-slate-500 font-mono">{data.acquisition.session_id.slice(0, 8)}&hellip;</span>
                    </div>
                  )}
                  <div className="flex justify-between py-1 border-b border-slate-800">
                    <span className="text-slate-400">Acquisition date</span>
                    <span className="text-slate-200">{new Date(data.member.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Ledger with notes */}
            <div className={cardCls}>
              <h3 className={sectionH3}>Points Ledger (last 20)</h3>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {data.ledger.map((e: { id: string; source_type: string; delta: number; note: string | null; source_id: string | null; created_at: string }) => (
                  <div key={e.id} className="text-xs py-1.5 border-b border-slate-800">
                    <div className="flex justify-between">
                      <span className="text-slate-300">{e.source_type}</span>
                      <span className={e.delta >= 0 ? 'text-green-400' : 'text-red-400'}>{e.delta >= 0 ? '+' : ''}{e.delta}</span>
                      <span className="text-slate-600">{new Date(e.created_at).toLocaleDateString()}</span>
                    </div>
                    {(e.note || e.source_id) && (
                      <p className="text-slate-500 mt-0.5 truncate">{e.note ?? e.source_id}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            </div>
            </div>
            {/* Deep dives — fraud, trust, attributes, sessions */}
            <div className={cardCls}>
              <div className="flex gap-1 mb-4">
                {(['fraud', 'trust', 'attributes', 'sessions'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      activeTab === tab
                        ? 'bg-primary text-white'
                        : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              {activeTab === 'fraud' && <FraudTab memberId={memberId} />}
              {activeTab === 'trust' && <TrustTab memberId={memberId} currentScore={data.member.trust_score ?? 10} />}
              {activeTab === 'attributes' && <AttributesTab memberId={memberId} />}
              {activeTab === 'sessions' && <SessionsTab memberId={memberId} />}
            </div>

            {/* Danger Zone */}
            <div className="bg-slate-900 rounded-xl border border-red-900/40 p-5">
              <h3 className="text-xs text-red-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Danger Zone
              </h3>
              <p className="text-xs text-slate-500 mb-2">This will permanently delete this member and all their data. Type DELETE to confirm.</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder="Type DELETE"
                  className={`flex-1 ${inputCls}`}
                />
                <button
                  onClick={() => void handleDelete()}
                  disabled={deleteConfirm !== 'DELETE' || deleting}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40 hover:bg-red-700"
                >
                  {deleting ? 'Deleting...' : 'Delete Member'}
                </button>
              </div>
            </div>
        </>
      )}
    </div>
  );
}

// ── Demographics panel ──────────────────────────────────────────────────────
// Aggregate breakdowns alongside the list/search — does not touch either.

type DemoBucket = { label: string; count: number };

function toBuckets(rows: { count: number }[], labelKey: 'band' | 'value' | 'bracket'): DemoBucket[] {
  return (rows as Record<string, unknown>[]).map((r) => ({ label: String(r[labelKey]), count: r.count as number }));
}

const GENDER_LABELS: Record<string, string> = { male: 'Male', female: 'Female', other: 'Other', prefer_not: 'Prefer not to say' };
const ETHNICITY_LABELS: Record<string, string> = {
  white: 'White', black: 'Black', asian: 'Asian', pacific_islander: 'Pacific Islander',
  native_american: 'Native American', hispanic: 'Hispanic', middle_eastern: 'Middle Eastern', other: 'Other',
};

function relabel(buckets: DemoBucket[], map: Record<string, string>): DemoBucket[] {
  return buckets.map((b) => ({ ...b, label: map[b.label] ?? b.label }));
}

function DemoBreak({ title, buckets }: { title: string; buckets: DemoBucket[] }) {
  const max = buckets.reduce((m, b) => Math.max(m, b.count), 0);
  return (
    <div className="bg-slate-800 rounded-xl p-4 min-w-0">
      <h3 className="text-xs uppercase tracking-wide text-slate-400 mb-3">{title}</h3>
      {buckets.length === 0 ? (
        <p className="text-xs text-slate-500">No data</p>
      ) : (
        <div className="space-y-2">
          {buckets.map((b) => (
            <div key={b.label} className="text-xs">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className="text-slate-300 truncate">{b.label}</span>
                <span className="text-slate-400 tabular-nums flex-shrink-0">{b.count.toLocaleString()}</span>
              </div>
              <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: max > 0 ? `${(b.count / max) * 100}%` : '0%' }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DemographicsPanel() {
  const { data, isLoading, error } = useQuery<MemberDemographics>({
    queryKey: ['admin', 'members', 'demographics'],
    queryFn: fetchMemberDemographics,
  });

  if (isLoading) return <div className="bg-slate-800 rounded-xl p-4 text-sm text-slate-400">Loading demographics…</div>;
  if (error || !data) return <div className="bg-slate-800 rounded-xl p-4 text-sm text-red-400">Failed to load demographics.</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide">Demographics</h2>
        <span className="text-xs text-slate-500">{data.total.toLocaleString()} members</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        <DemoBreak title="Age" buckets={toBuckets(data.age, 'band')} />
        <DemoBreak title="Gender" buckets={relabel(toBuckets(data.gender, 'value'), GENDER_LABELS)} />
        <DemoBreak title="Region" buckets={toBuckets(data.region, 'value')} />
        <DemoBreak title="Household income" buckets={toBuckets(data.hhi, 'bracket')} />
        <DemoBreak title="Ethnicity" buckets={relabel(toBuckets(data.ethnicity, 'value'), ETHNICITY_LABELS)} />
      </div>
    </div>
  );
}

export default function AdminMembers() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [identityFilter, setIdentityFilter] = useState<'all' | 'true' | 'false'>('all');
  const navigate = useNavigate();

  // Search-first: don't list all members on load. The list appears once the admin
  // searches OR selects an identity filter (Verified / Not verified). The
  // demographics summary + filter tabs render regardless.
  const listActive = search.trim() !== '' || identityFilter !== 'all';

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'members', page, search, identityFilter],
    queryFn: () => fetchAdminMembers(page, search, identityFilter === 'all' ? undefined : identityFilter),
    enabled: listActive,
  });

  const members = data?.members ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / 25);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold font-display text-white">Members</h1>
        {listActive && <span className="text-sm text-slate-400">{total} matching</span>}
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }}
            placeholder="Search email or name..."
            className="w-full bg-slate-800 text-white rounded-lg pl-10 pr-4 py-2.5 text-sm border border-slate-700 placeholder:text-slate-500"
          />
        </div>
        <button onClick={() => { setSearch(searchInput); setPage(1); }} className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium">Search</button>
      </div>

      {/* Identity filter (Didit, Phase B) */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500 uppercase tracking-wide">Identity</span>
        <div className="inline-flex rounded-lg border border-slate-700 overflow-hidden">
          {([['all', 'All'], ['true', 'Verified'], ['false', 'Not verified']] as const).map(([value, label]) => (
            <button
              key={value}
              onClick={() => { setIdentityFilter(value); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                identityFilter === value ? 'bg-primary text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <DemographicsPanel />

      {!listActive ? (
        <div className="bg-slate-800 rounded-xl p-10 text-center">
          <p className="text-slate-300 font-medium">Search by email or name</p>
          <p className="text-slate-500 text-sm mt-1">Or pick an identity filter above to list members.</p>
        </div>
      ) : isLoading ? (
        <div className="text-slate-400">Loading...</div>
      ) : (
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wide border-b border-slate-700">
                <th className="px-4 py-3">Email / Phone</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3 text-right">Points</th>
                <th className="px-4 py-3 text-right">Streak</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Identity</th>
                <th className="px-4 py-3">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {members.map((m: { id: string; email: string; display_name: string | null; total_points: number; streak_current: number; status: string; phone_number: string | null; created_at: string; identity_verified?: boolean }) => (
                <tr
                  key={m.id}
                  onClick={() => navigate(`/admin/members/${m.id}`)}
                  className="hover:bg-slate-700/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-white">{m.email || m.phone_number || '\u2014'}</td>
                  <td className="px-4 py-3 text-slate-300">{m.display_name ?? '\u2014'}</td>
                  <td className="px-4 py-3 text-right text-white tabular-nums">{m.total_points.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-amber-400 tabular-nums">{m.streak_current}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{m.phone_number ?? '\u2014'}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${m.status === 'active' ? 'bg-green-900/50 text-green-400' : 'bg-slate-700 text-slate-400'}`}>
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {m.identity_verified
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/50 text-emerald-400">Verified</span>
                      : <span className="text-xs text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-400">{new Date(m.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-400">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-1 rounded bg-slate-800 disabled:opacity-40">Prev</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-1 rounded bg-slate-800 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

    </div>
  );
}
